import { useSyncExternalStore } from 'react';
import api from '../api/axios';

/**
 * Post & report status for Rustle It support (tdp-api docs/support-access-plan.md
 * §9a): the STATE of posts, images and reports, never their content. Only in a
 * support session whose grant carries the Admin's opt-in; the API refuses
 * otherwise (403 SUPPORT_DIAGNOSTICS_NOT_GRANTED).
 */

export interface DiagPerson { id: number; name: string; isExternal: boolean }

export interface DiagPost {
  ref: string;
  id: number;
  postDate: string;
  createdAt: string;
  updatedAt: string | null;
  author: DiagPerson | null;
  classroomId: number | null;
  status: 'published' | 'unpublished';
  children: string[];
  images: { count: number; missingThumbnail: number };
  ai: {
    /** done: rewrite made · skipped: short or judged not worth analysing (normal) ·
     *  failed: an error was recorded · not_run: no AI processing yet. */
    status: AiStatus;
    processedAt: string | null;
    /** Full AI only (null on a rewrite-only plan, which makes neither). */
    tags: boolean | null;
    searchIndex: boolean | null;
  };
}

export type AiStatus = 'done' | 'skipped' | 'failed' | 'not_run';
/** What the organisation's plan runs on each post. */
export type AiPlan = 'full' | 'rewrite_only';

export interface DiagImage {
  id: number;
  type: string | null;
  fileType: string | null;
  uploadedAt: string | null;
  fileRecorded: boolean;
  thumbnail: boolean;
  edited: boolean;
  editedThumbnail: boolean | null;
  showingEdited: boolean | null;
  excludedFromReport: boolean;
}

/** Facts about one of a post's texts — never the text itself. */
export interface DiagTextFacts {
  field: 'text' | 'published' | 'ai_rewrite';
  present: boolean;
  chars: number | null;
  words: number | null;
  lines: number | null;
  emoji: number | null;
  invisible: number | null;
  unusual: number | null;
  /** HTML tags (<br/>, </p>) and codes (&amp;), as pasted from a web page or email. */
  htmlTags: number | null;
  /** A tag, link or handler that could run or load something. */
  possibleScript: boolean;
  /** [child_name] etc., swapped for the real name when read. */
  childPlaceholders: number | null;
  /** Other [bracketed] tokens: readers would see these as broken names. */
  otherBracketTokens: number | null;
  /** Published text only. */
  source?: 'original' | 'ai_rewrite' | null;
  editedAfterChoosing?: boolean | null;
}

export interface DiagReport {
  ref: string;
  id: number;
  child: string;
  reportType: string | null;
  templateId: number | null;
  templateName: string | null;
  period: { start: string | null; end: string | null };
  reportDate: string | null;
  createdAt: string;
  author: DiagPerson | null;
  fileRecorded: boolean;
  passwordProtected: boolean;
}

export interface DiagError {
  at: string;
  /** The event name, e.g. post.publish_failed. */
  code: string;
  errName: string | null;
  errCode: string | null;
  requestId: string | null;
  post: string | null;
  uploadId: number | null;
  report: string | null;
  templateId: number | null;
  children: string[];
  period: { start: string | null; end: string | null } | null;
}

export interface DiagAttention {
  since: string;
  aiPlan: AiPlan;
  errors: DiagError[];
  errorsTotal: number;
  postsWithIncompleteImages: DiagPost[];
  postsWithIncompleteImagesTotal: number;
  note: string;
}

export interface DiagPostFilters { ids?: string; from?: string; to?: string; author?: string; child?: string; classroom?: string }
export interface DiagReportFilters { ids?: string; from?: string; to?: string; child?: string; template?: string }

/** Drop empty filters so the API sees only what was given. */
function params<T extends object>(f: T): Record<string, string> {
  return Object.fromEntries(Object.entries(f).filter(([, v]) => typeof v === 'string' && v.trim() !== '')
    .map(([k, v]) => [k, (v as string).trim()]));
}

export class SupportDiagnosticsService {
  static async attention(days: number): Promise<DiagAttention> {
    return (await api.get('/support-diagnostics/attention', { params: { days } })).data;
  }

  static async posts(filters: DiagPostFilters): Promise<{ aiPlan: AiPlan; posts: DiagPost[]; total: number; note: string }> {
    return (await api.get('/support-diagnostics/posts', { params: params(filters) })).data;
  }

  static async post(ref: string): Promise<{ aiPlan: AiPlan; post: DiagPost; images: DiagImage[]; text: DiagTextFacts[]; errors: DiagError[]; note: string }> {
    return (await api.get(`/support-diagnostics/posts/${encodeURIComponent(ref)}`)).data;
  }

  static async reports(filters: DiagReportFilters): Promise<{ reports: DiagReport[]; total: number; errors?: DiagError[]; note?: string }> {
    return (await api.get('/support-diagnostics/reports', { params: params(filters) })).data;
  }
}

// ── Is it switched on? ─────────────────────────────────────────────────────
// Set from the support banner's once-a-minute grant check, so the menu item
// and page follow an Admin switching it on or off mid-session. null until
// the first check has answered.

let allowed: boolean | null = null;
const listeners = new Set<() => void>();

export function setSupportDiagnosticsAllowed(v: boolean | null): void {
  if (v === allowed) return;
  allowed = v;
  listeners.forEach(l => l());
}

export function useSupportDiagnosticsAllowed(): boolean | null {
  return useSyncExternalStore(
    cb => { listeners.add(cb); return () => listeners.delete(cb); },
    () => allowed,
  );
}
